import React, { useEffect, useRef, useState } from "react";
import { resolveBranding } from "../presentationBranding";
import StudioProfileMenu from "./StudioProfileMenu";

const STATUS = {
  VERIFIED: "var(--cockpit-status-verified, #3ecf8e)",
  DEGRADED: "var(--cockpit-status-degraded, #f2b441)",
  DOWN: "var(--cockpit-status-down, #e5384f)",
  UNKNOWN: "var(--cockpit-status-unknown, #85838d)",
};

function roleFor(user) {
  const roles = (Array.isArray(user?.roles) ? user.roles : []).map((role) => String(role).toUpperCase());
  if (roles.includes("PLATFORM_ADMIN")) return "Platform Admin";
  if (roles.includes("ADMIN")) return "Admin";
  if (roles.includes("RECEPTIONIST")) return "Receptionist";
  if (roles.includes("TRAINER")) return "Trainer";
  if (roles.includes("MANAGER")) return "Manager";
  if (roles.includes("OWNER")) return "Owner";
  return "Member";
}

function normalizeHealth(value) {
  const health = String(typeof value === "string" ? value : value?.health || value?.status || value?.state || "").toUpperCase();
  if (value?.online === false || ["DOWN", "FAILED", "UNHEALTHY", "STOPPED", "ERROR", "CRITICAL"].includes(health)) return "DOWN";
  if (["DEGRADED", "WARNING", "PARTIAL"].includes(health) || value?.ready === false) return "DEGRADED";
  if (["HEALTHY", "READY", "VERIFIED"].includes(health) || value?.ready === true) return "VERIFIED";
  return "UNKNOWN";
}

function capabilityStatus(capabilities, matcher) {
  const states = Object.entries(capabilities || {})
    .filter(([name]) => matcher.test(name.toLowerCase()))
    .map(([, value]) => normalizeHealth(value));
  if (!states.length) return "UNKNOWN";
  if (states.includes("DOWN")) return "DOWN";
  if (states.includes("DEGRADED")) return "DEGRADED";
  if (states.includes("UNKNOWN")) return "UNKNOWN";
  return "VERIFIED";
}

function aggregateStatus(segments) {
  const states = segments.map((segment) => segment.status);
  if (states.includes("DOWN")) return "DOWN";
  if (states.includes("DEGRADED")) return "DEGRADED";
  if (states.includes("UNKNOWN")) return "UNKNOWN";
  return states.length ? "VERIFIED" : "UNKNOWN";
}

function applicationHealthStatus(application) {
  if (!application || application.enabled === false) return "UNKNOWN";
  const state = String(application.application_status || "").toUpperCase();
  if (["UNAVAILABLE", "NOT_INSTALLED", "NOT_STARTED"].includes(state)) return "DOWN";
  if (["MAINTENANCE", "UPGRADE_REQUIRED"].includes(state)) return "DEGRADED";
  if (application.readiness === "BLOCKED") return "DOWN";
  if (application.readiness === "DEGRADED") return "DEGRADED";
  if (application.readiness === "READY" && application.application_status === "VERIFIED") return "VERIFIED";
  // A responding HTTP endpoint alone does not establish application acceptance.
  return "UNKNOWN";
}

function applicationStateLabel(application) {
  if (!application) return "Directory unavailable";
  if (!application.enabled) return "Disabled";
  if (!application.entitled) return "Not authorized";
  if (application.readiness === "BLOCKED") return "Deployment blocked";
  const state = String(application.application_status || "").toUpperCase();
  if (state === "UNAVAILABLE") return "Application unavailable";
  if (state === "NOT_INSTALLED") return "Not installed";
  if (state === "NOT_STARTED") return "Not started";
  if (state === "MAINTENANCE") return "Maintenance mode";
  if (state === "UPGRADE_REQUIRED") return "Upgrade required";
  if (!application.open_url) return "Route not configured";
  if (application.readiness === "READY" && state === "VERIFIED") return "Verified";
  if (state === "RESPONDING") return "Responding; login not verified";
  return "Not verified";
}

function serviceDock(applications, stale = false, loading = false) {
  const services = [
    { id: "jellyfin", label: "Watch & Listen" },
    { id: "immich", label: "Moments" },
    { id: "nextcloud", label: "Files" },
    { id: "humhub", label: "Community" },
  ];
  const directory = Array.isArray(applications) ? applications : [];
  return services.map((service) => {
    const application = stale ? null : directory.find((item) => String(item?.id || "").toLowerCase() === service.id);
    const entitled = application?.entitled === true;
    const enabled = application?.enabled === true;
    const href = entitled && enabled && application?.open_url ? application.open_url : "";
    const detail = loading
      ? "Checking application directory; access not confirmed"
      : stale ? "Application directory stale; access not confirmed"
        : application ? applicationStateLabel(application) : "Not registered";
    return {
      label: service.label,
      href: href || undefined,
      external: true,
      disabled: loading || stale || !href,
      detail,
      serviceId: service.id,
    };
  });
}

function segmentRing(segments) {
  if (!segments.length) return "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)";
  const slice = 360 / segments.length;
  const gap = Math.min(3, slice / 8);
  const stops = [];
  segments.forEach((segment, index) => {
    const start = index * slice;
    const end = start + slice - gap;
    stops.push(STATUS[segment.status] + " " + start + "deg " + end + "deg");
    if (index < segments.length - 1) stops.push("transparent " + end + "deg " + (start + slice) + "deg");
  });
  return "conic-gradient(" + stops.join(", ") + ")";
}

function formatTime(value) {
  if (!value) return "Time not scheduled";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Time not scheduled" : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function readNonNegativeCount(value) {
  if (value === null || value === undefined || value === "") return null;
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : null;
}

function formatElapsed(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainder = total % 60;
  return [hours, minutes, remainder].map((part) => String(part).padStart(2, "0")).join(":");
}

function groupedSegment(name, sources, matcher) {
  const matches = sources.filter((segment) => matcher.test(String(segment.name || "").toLowerCase()));
  return { name, status: matches.length ? aggregateStatus(matches) : "UNKNOWN", evidenceCount: matches.length };
}

function countSpatialSpaces(spaces) {
  if (!spaces || typeof spaces !== "object") return null;
  const entries = Array.isArray(spaces) ? spaces.map((space, index) => [String(index), space]) : Object.entries(spaces);
  return entries.reduce((total, [, space]) => total + 1 + (countSpatialSpaces(space?.subspaces) || 0), 0);
}

function adminSegmentSet(capabilitySegments, appSegments, selectedSet) {
  if (selectedSet === "Platform") {
    return [
      groupedSegment("Media", [...capabilitySegments, ...appSegments], /media|jellyfin|immich|nextcloud|humhub|display|audio/),
      groupedSegment("AI", capabilitySegments, /\bai\b|inference|model|gpu|roc/),
      groupedSegment("Events", capabilitySegments, /event|scheduler|stream|redpanda/),
      groupedSegment("Governance", capabilitySegments, /governance|policy|iam|identity|access/),
      groupedSegment("Knowledge", capabilitySegments, /knowledge|index|catalog/),
      groupedSegment("Recovery & durability", capabilitySegments, /recovery|snapshot|snapraid|backup|parity|storage/),
    ];
  }
  return [
    groupedSegment("Storage", capabilitySegments, /storage|disk|pool|filesystem|snapraid|snapshot|parity/),
    groupedSegment("Runtime", capabilitySegments, /runtime|container|docker|postgres|redis|redpanda|service/),
    groupedSegment("Devices", capabilitySegments, /device|display|audio|lighting|sensor|environment|room|media/),
    groupedSegment("Apps", appSegments, /.*/),
    groupedSegment("Network", capabilitySegments, /network|dns|tls|ingress|proxy|http/),
  ];
}

function getRoleContent(props) {
  const role = props.role;
  const summary = props.summary && typeof props.summary === "object" && !Array.isArray(props.summary) ? props.summary : {};
  const applications = Array.isArray(props.applications) ? props.applications.filter((item) => item && typeof item === "object" && !Array.isArray(item)) : [];
  const eventState = props.eventState;
  const dominantState = props.dominantState;
  const readiness = props.readiness && typeof props.readiness === "object" && !Array.isArray(props.readiness) ? props.readiness : {};
  const incidents = Array.isArray(props.incidents) ? props.incidents.filter((item) => item && typeof item === "object" && !Array.isArray(item)) : [];
  const cls = props.cls && typeof props.cls === "object" && !Array.isArray(props.cls) ? props.cls : null;
  const schedule = Array.isArray(props.schedule) ? props.schedule.filter((item) => item && typeof item === "object" && !Array.isArray(item)) : [];
  const readinessCapabilities = readiness.capabilities && typeof readiness.capabilities === "object" && !Array.isArray(readiness.capabilities) ? readiness.capabilities : {};
  const spatialCapabilities = summary.spatial?.capabilities && typeof summary.spatial.capabilities === "object" && !Array.isArray(summary.spatial.capabilities) ? summary.spatial.capabilities : {};
  const capabilities = spatialCapabilities;
  const selectedAdminSet = props.adminSegmentSet === "Platform" ? "Platform" : "Infrastructure";
  const applicationsStale = props.applicationsStale === true;
  const applicationsLoading = props.applicationsLoading === true;
  const active = schedule
    .filter((event) => {
      const start = event?.scheduled_start || event?.start_time;
      return start && new Date(start).getTime() >= Date.now();
    })
    .sort((a, b) => new Date(a.scheduled_start || a.start_time) - new Date(b.scheduled_start || b.start_time))[0];
  const currentOrNext = cls?.id ? cls : active;
  const count = schedule.length;

  if (role === "Platform Admin" || role === "Admin") {
    const capabilitySegments = Object.entries(capabilities || {}).map(([name, value]) => ({ name: name.replaceAll("_", " "), status: normalizeHealth(value) }));
    const appSegments = applications.map((app) => ({
      name: app.name || app.id || "Application",
      status: applicationsStale ? "UNKNOWN" : applicationHealthStatus(app),
    }));
    const groupedSegments = adminSegmentSet(capabilitySegments, appSegments, selectedAdminSet);
    const segments = groupedSegments.some((segment) => segment.evidenceCount > 0) ? groupedSegments : [];
    const verified = segments.filter((segment) => segment.status === "VERIFIED").length;
    const degraded = segments.filter((segment) => segment.status === "DEGRADED").length;
    const down = segments.filter((segment) => segment.status === "DOWN").length;
    const unknown = segments.filter((segment) => segment.status === "UNKNOWN").length;
    const topologySpaces = countSpatialSpaces(summary?.spatial?.spaces);
    const capabilityCount = Object.keys(capabilities || {}).length;
    const degradedCount = segments.filter((segment) => ["DOWN", "DEGRADED"].includes(segment.status)).length;
    const primary = down || incidents.length ? "Review incident" : "Run doctor";
    const primaryTarget = incidents.length ? "#studio-attention" : down ? "#studio-capabilities" : null;
    return {
      label: "PLATFORM HEALTH",
      value: segments.length ? verified + "/" + segments.length : "—",
      unit: segments.length ? "segments verified" : "no data",
      summary: segments.length ? [verified + " verified", degraded + " degraded", down + " down", unknown + " unknown"].join(" · ") : "No authoritative capability or application health data is available.",
      status: down ? "DOWN" : degraded || incidents.length ? "DEGRADED" : segments.length && unknown === 0 ? "VERIFIED" : "UNKNOWN",
      ring: segmentRing(segments),
      contextTitle: selectedAdminSet.toUpperCase(),
      context: [
        ...segments.slice(0, 8).map((segment) => ({ label: segment.name, note: segment.status, status: segment.status })),
        ...(segments.length > 8 ? [{ label: "+" + (segments.length - 8) + " more", note: "Open full view", status: "UNKNOWN", target: "#studio-capabilities" }] : []),
      ],
      primary,
      primaryTarget,
      primaryAction: down || incidents.length ? "navigate" : "doctor",
      chips: [
        { title: "Digital Twin", detail: (topologySpaces === null ? "Topology count not supplied" : topologySpaces + " spaces") + " · " + (capabilityCount ? capabilityCount + " capability records" : "Capability count unavailable"), target: "#studio-spaces", state: topologySpaces !== null && summary?.state_authoritative === true ? topologySpaces + " spaces" : "Open" },
        { title: "Health & Incidents", detail: "Open incidents and capability degradation", target: "#studio-attention", state: incidents.length ? incidents.length + " open" : degradedCount ? degradedCount + " degraded" : summary?.state_authoritative === true ? "None reported" : "Unknown" },
        { title: "Journals & Logs", detail: "Available diagnostics and audit context; log recency is not supplied", target: "#studio-diagnostics", state: "Open" },
        { title: "Devices & Capabilities", detail: "Room readiness and reported capability records", target: "#studio-capabilities", state: summary?.state_authoritative === true ? capabilityCount + " reported" : "Unknown" },
      ],
      dock: [{ label: "Applications", target: "#studio-applications" }, { label: "Capabilities", target: "#studio-capabilities" }, { label: "Schedule", target: "#studio-schedule" }, { label: "Access & accounts", target: "#studio-access" }, { label: "Site Designer", href: "/operator/site-designer" }, ...serviceDock(applications, applicationsStale, applicationsLoading)],
    };
  }

  if (role === "Receptionist") {
    const start = currentOrNext?.scheduled_start || currentOrNext?.start_time;
    const minutes = start ? Math.round((new Date(start).getTime() - Date.now()) / 60000) : null;
    const imminent = Number.isFinite(minutes) && minutes >= 0 && minutes <= 60;
    const title = currentOrNext ? (currentOrNext.event_type || currentOrNext.type || "Next class") : "No class scheduled";
    const roster = Array.isArray(currentOrNext?.participant_ids) ? currentOrNext.participant_ids : [];
    const rosterCount = currentOrNext === cls ? (readNonNegativeCount(cls?.scheduled_clients) ?? roster.length) : roster.length;
    const attendance = Array.isArray(currentOrNext?.attendance) ? currentOrNext.attendance : null;
    const attendanceCount = roster.length && attendance
      ? new Set(attendance.filter((clientId) => roster.includes(clientId))).size
      : null;
    const checkedInCount = currentOrNext === cls
      ? (readNonNegativeCount(cls?.checked_in_clients) ?? attendanceCount)
      : attendanceCount;
    const attendanceFraction = rosterCount > 0 && Number.isFinite(checkedInCount)
      ? Math.max(0, Math.min(1, checkedInCount / rosterCount))
      : null;
    const attendanceDegrees = attendanceFraction === null ? 0 : Math.round(attendanceFraction * 360);
    const attendanceLabel = currentOrNext && attendanceFraction !== null
      ? Math.min(rosterCount, Math.max(0, checkedInCount)) + "/" + rosterCount + " checked in"
      : "roster not supplied";
    return {
      label: "NEXT CLASS · " + title + " · " + (currentOrNext?.room_id || "ROOM TBD"),
      value: currentOrNext ? (minutes !== null && minutes >= 0 ? (minutes > 90 ? Math.floor(minutes / 60) + "h " + (minutes % 60) + "m" : String(minutes)) : formatTime(start)) : "—",
      unit: currentOrNext && minutes !== null && minutes >= 0 ? (minutes > 90 ? "until class starts" : "minutes to start") : currentOrNext ? "scheduled time" : "schedule unavailable",
      summary: currentOrNext ? title + " · " + formatTime(start) + " · " + (currentOrNext.room_id || "Room not assigned") + " · " + attendanceLabel : "No upcoming class is present in the current schedule projection.",
      status: incidents.length ? "DEGRADED" : "UNKNOWN",
      ring: attendanceFraction !== null ? `conic-gradient(var(--red) 0deg ${attendanceDegrees}deg, var(--line) ${attendanceDegrees}deg 360deg)` : "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)",
      contextTitle: "TODAY'S SCHEDULE",
      context: [
        ...(schedule || []).slice(0, 5).map((event) => ({ label: event.event_type || event.type || "Class", note: formatTime(event.scheduled_start) + " · " + (event.room_id || "Room unassigned"), status: normalizeHealth({ status: event.state }) })),
        ...((schedule || []).length > 5 ? [{ label: "+" + (schedule.length - 5) + " more", note: "Open full schedule", status: "UNKNOWN", target: "#studio-schedule" }] : []),
      ],
      primary: imminent && cls?.id ? "Check in clients" : "Schedule a class",
      primaryTarget: imminent && cls?.id ? "#studio-checkin" : "#studio-schedule",
      chips: [
        { title: "Schedule a class", detail: "Create and assign a session", target: "#studio-schedule", state: "Open" },
        { title: "Clients & check-in", detail: "Review the current roster", target: "#studio-checkin", state: cls?.id ? "Current roster" : "No current roster" },
        { title: "Studio spaces", detail: "Explore rooms and capabilities", target: "#studio-spaces", state: "Open" },
        { title: "Ambient music", detail: "Console-zone playback controls", target: "#studio-spaces", state: "Open" },
      ],
      dock: [{ label: "Studio map", target: "#studio-spaces" }, { label: "Applications", target: "#studio-applications" }, { label: "Signup requests", disabled: true, detail: "Not authorized for the Receptionist role" }, ...serviceDock(applications, applicationsStale, applicationsLoading)],
    };
  }

  if (role === "Trainer") {
    const displayStatus = capabilityStatus(readinessCapabilities, /display|screen|projector/);
    const audioStatus = capabilityStatus(readinessCapabilities, /audio|speaker|sound/);
    const mediaStatus = capabilityStatus(readinessCapabilities, /media|playback|jellyfin/);
    const lightingStatus = capabilityStatus(readinessCapabilities, /light|lighting/);
    const running = ["RUNNING", "STARTED"].includes(eventState);
    const paused = eventState === "PAUSED";
    const finished = eventState === "FINISHED";
    const ready = eventState === "SCHEDULED" && cls?.startable === true && readiness?.ready === true && summary?.state_authoritative === true;
    const noClass = !currentOrNext && !running && !paused && !finished;
    const finishedReady = finished && incidents.length === 0 && readiness?.ready === true && summary?.state_authoritative === true;
    const state = finished ? (finishedReady ? "CLASS COMPLETE — STUDIO READY" : "CLASS COMPLETE") : running ? "CLASS IN PROGRESS" : paused ? "CLASS PAUSED" : ready ? "READY TO START" : dominantState || "NO CLASS";
    const expectedStart = currentOrNext?.start_time || currentOrNext?.scheduled_start;
    const expectedEnd = currentOrNext?.end_time || currentOrNext?.scheduled_end;
    const durationMs = expectedStart && expectedEnd
      ? new Date(expectedEnd).getTime() - new Date(expectedStart).getTime()
      : NaN;
    const expectedSeconds = Number.isFinite(durationMs) && durationMs > 0
      ? Math.floor(durationMs / 1000)
      : null;
    const timerKnown = props.timerKnown === true;
    const elapsedSeconds = !timerKnown ? null : paused
      ? (props.timerData?.elapsed_seconds ?? props.displaySeconds)
      : props.displaySeconds;
    const progressFraction = timerKnown && expectedSeconds > 0 && Number.isFinite(Number(elapsedSeconds))
      ? Math.min(1, Math.max(0, Number(elapsedSeconds) / expectedSeconds))
      : null;
    const progressDegrees = progressFraction === null ? 0 : Math.round(progressFraction * 360);
    const progressRing = progressFraction === null
      ? "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)"
      : `conic-gradient(var(--ok) 0deg ${progressDegrees}deg, var(--line) ${progressDegrees}deg 360deg)`;
    return {
      label: "TRAINER · CLASS STATUS",
      value: finished ? "COMPLETE" : running ? timerKnown ? formatElapsed(elapsedSeconds) : "—" : paused ? "PAUSED" : ready ? "READY" : currentOrNext ? "UP NEXT" : "—",
      unit: finished ? "session finished" : running && expectedSeconds !== null ? (timerKnown ? "elapsed / " + formatElapsed(expectedSeconds) : "elapsed unavailable · expected " + formatElapsed(expectedSeconds)) : currentOrNext ? (currentOrNext.event_type || currentOrNext.type || "Class") : "no assigned class reported",
      summary: finished ? (finishedReady ? "CLASS COMPLETE — Studio READY" : incidents.length ? "CLASS COMPLETE — review studio attention." : "CLASS COMPLETE — readiness unverified.") : state + (running && !timerKnown ? " · Elapsed timer evidence unavailable." : paused && !timerKnown ? " · Timer evidence unavailable." : "") + (readiness?.ready === false && !running && !paused ? " · Required readiness is not verified." : ""),
      status: incidents.length ? "DEGRADED" : readiness?.ready === true ? "VERIFIED" : "UNKNOWN",
      ring: running || paused ? progressRing : finishedReady ? "conic-gradient(var(--cockpit-status-verified, #3ecf8e) 0deg 360deg)" : "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)",
      contextTitle: "CLASS CONTEXT",
      context: [
        { label: "Room readiness", note: readiness?.ready === true && summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN", status: readiness?.ready === true && summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
        { label: "Roster", note: cls?.roster?.length ? cls.roster.length + " assigned" : "Not supplied", status: cls?.roster?.length && summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
        { label: "Incidents", note: incidents.length ? incidents.length + " open" : "None reported", status: incidents.length ? "DEGRADED" : "UNKNOWN" },
      ],
      primary: finished || noClass ? "View today's schedule" : running ? "Pause class" : paused ? "Resume class" : ready ? "Start class" : "Review readiness",
      primaryTarget: finished || noClass ? "#studio-schedule" : "#studio-current-class",
      chips: [
        { title: "Class music", detail: "Assigned playlist and supported playback controls", target: "#studio-current-class", state: cls?.playlist_id ? "Playlist assigned" : "Review class" },
        { title: "Room controls", detail: "Capability controls are restricted for this role", disabled: true, state: "Planned" },
        { title: "Roster & notes", detail: "Review the assigned class roster", target: "#studio-checkin", state: cls?.roster?.length ? cls.roster.length + " assigned" : "Unknown" },
        { title: "Class library", detail: "Trainer class library destination is not available in this portal view", disabled: true, state: "Planned" },
      ],
      dock: [
        { label: "Display", detail: displayStatus + " · readiness only", disabled: true },
        { label: "Audio", detail: audioStatus + " · readiness only", disabled: true },
        { label: "Media", detail: mediaStatus + " · readiness only", disabled: true },
        { label: "Lighting", detail: lightingStatus + " · readiness only", disabled: true },
        { label: "Studio map", detail: "Not available to Trainer role", disabled: true },
        { label: "Report an issue", target: "#studio-attention" },
        ...serviceDock(applications, applicationsStale, applicationsLoading),
      ],
    };
  }

  if (role === "Owner") {
    const scheduledAttendanceCount = readNonNegativeCount(summary?.class?.scheduled_clients);
    const checkedAttendanceCount = readNonNegativeCount(summary?.class?.checked_in_clients);
    const attendanceAvailable = Boolean(summary?.class?.id) && summary?.state_authoritative === true && scheduledAttendanceCount !== null && checkedAttendanceCount !== null;
    const attendanceMismatch = attendanceAvailable && checkedAttendanceCount > scheduledAttendanceCount;
    const attendanceNote = attendanceAvailable
      ? attendanceMismatch ? checkedAttendanceCount + "/" + scheduledAttendanceCount + " reported · count mismatch" : checkedAttendanceCount + "/" + scheduledAttendanceCount + " checked in"
      : "Not supplied";
    if (props.ownerMode) {
      const communityApp = (applications || []).find((item) => item.id === "humhub");
      const communityHref = !applicationsLoading && !applicationsStale && communityApp?.entitled === true && communityApp?.enabled === true && communityApp.open_url
        ? communityApp.open_url
        : "";
      return {
        label: "OWNER · TODAY", value: String(count), unit: "scheduled classes",
        summary: incidents.length ? incidents.length + " attention item(s) need review." : count ? "Today's schedule is available in the current studio projection." : "No schedule summary is currently available.",
        status: incidents.length ? "DEGRADED" : "UNKNOWN",
        ring: incidents.length ? "conic-gradient(var(--warn) 0deg 120deg, var(--line) 120deg 360deg)" : "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)",
        contextTitle: "TODAY AT THE STUDIO",
        context: [
          { label: "Scheduled classes", note: String(count), status: summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
          { label: "Attendance", note: attendanceNote, status: attendanceAvailable ? attendanceMismatch ? "DEGRADED" : "VERIFIED" : "UNKNOWN" },
          { label: "Attention items", note: String(incidents.length), status: incidents.length ? "DEGRADED" : summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
        ],
        primary: "Open membership review", primaryTarget: "#owner-membership",
        chips: [
          { title: "Studio summary", detail: "Current schedule projection", target: "#owner-membership", state: count ? count + " listed" : "No data" },
          { title: "Attendance", detail: "Aggregate count is shown in the summary; detailed attendance screen is unavailable in this owner route", disabled: true, state: attendanceAvailable ? attendanceNote : "Planned" },
          { title: "Classes", detail: "Full class management view not wired here yet", disabled: true, state: "Planned" },
          { title: "Community", serviceId: "humhub", detail: communityApp?.purpose || "Community service", href: communityHref || undefined, disabled: !communityHref, state: applicationsLoading ? "Checking application directory" : applicationsStale ? "Application directory stale" : applicationStateLabel(communityApp) },
        ],
        dock: [{ label: "Membership", target: "#owner-membership" }, ...serviceDock(applications, applicationsStale, applicationsLoading)],
      };
    }
    return {
      label: "OWNER · TODAY",
      value: String(count),
      unit: "scheduled classes",
      summary: incidents.length ? incidents.length + " attention item(s) need review." : count ? "Today's schedule is available from the studio projection." : "No schedule summary is currently available.",
      status: incidents.length ? "DEGRADED" : "UNKNOWN",
      ring: incidents.length ? "conic-gradient(var(--warn) 0deg 120deg, var(--line) 120deg 360deg)" : "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)",
      contextTitle: "TODAY AT THE STUDIO",
      context: [
        { label: "Scheduled classes", note: String(count), status: summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
        { label: "Attendance", note: attendanceNote, status: attendanceAvailable ? attendanceMismatch ? "DEGRADED" : "VERIFIED" : "UNKNOWN" },
        { label: "Attention items", note: String(incidents.length), status: incidents.length ? "DEGRADED" : summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
      ],
      primary: "View today's schedule",
      primaryTarget: "#studio-schedule",
      chips: [
        { title: "Studio summary", detail: "Today's classes and state", target: "#studio-schedule", state: count ? count + " listed" : "No data" },
        { title: "Attendance", detail: "Review current attendance", target: "#studio-checkin", state: "Open" },
        { title: "Exceptions", detail: "Review operational attention", target: "#studio-attention", state: incidents.length ? incidents.length + " open" : "No data" },
        { title: "Membership", detail: "Review signup requests", target: "#studio-access", state: "Open" },
      ],
      dock: [{ label: "Applications", target: "#studio-applications" }, { label: "Studio map", target: "#studio-spaces" }, { label: "Schedule", target: "#studio-schedule" }, ...serviceDock(applications, applicationsStale, applicationsLoading)],
    };
  }

  if (role === "Member") {
    const next = summary?.member_next_class || summary?.my_next_class || null;
    const expected = [
      { id: "jellyfin", title: "Watch & Listen" },
      { id: "immich", title: "Moments" },
      { id: "nextcloud", title: "Files" },
      { id: "humhub", title: "Community" },
    ];
    const available = expected.map((service) => {
      const app = applications.find((item) => String(item.id || "").toLowerCase() === service.id);
      const entitled = !applicationsLoading && !applicationsStale && app && (app.entitled === true || app.available_to_user === true || app.access?.allowed === true);
      const href = entitled && app.enabled === true && app.open_url ? app.open_url : "";
      const state = applicationsLoading ? "Checking application directory" : applicationsStale ? "Application directory stale" : applicationStateLabel(app);
      return {
        title: service.title,
        serviceId: service.id,
        detail: app?.purpose || "Studio service",
        href,
        disabled: !href,
        state,
      };
    });
    return {
      label: "YOUR STUDIO",
      value: next ? formatTime(next.scheduled_start || next.start_time) : "—",
      unit: next ? (next.event_type || next.type || "Next class") : "no upcoming class reported",
      summary: next ? "Your next class is shown from the current studio projection." : "No upcoming class is currently available in your studio view.",
      status: "UNKNOWN",
      ring: "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)",
      contextTitle: "YOUR NEXT VISIT",
      context: next ? [
        { label: "Class", note: next.event_type || next.type || "Scheduled", status: summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
        { label: "Start", note: formatTime(next.scheduled_start || next.start_time), status: summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
        { label: "Room", note: next.room_id || "Not assigned", status: next.room_id && summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
      ] : [],
      primary: "Explore studio spaces",
      primaryTarget: "#studio-spaces",
      chips: available.map((item) => ({ ...item, target: "#studio-applications" })),
      dock: [{ label: "Studio map", target: "#studio-spaces" }, { label: "Profile", href: "/profile" }],
    };
  }

  if (role === "Manager") {
    const upcoming = (schedule || [])
      .filter((event) => {
        const start = event?.scheduled_start || event?.start_time;
        return start && new Date(start).getTime() >= Date.now();
      })
      .sort((a, b) => new Date(a.scheduled_start || a.start_time) - new Date(b.scheduled_start || b.start_time));
    const context = [
      ...incidents.slice(0, 4).map((incident) => ({
        label: incident.capability ? "Issue · " + incident.capability : "Operational exception",
        note: String(incident.message || "Review required"), status: "DEGRADED", target: "#studio-attention",
      })),
      ...upcoming.slice(0, Math.max(0, 4 - Math.min(4, incidents.length))).map((event) => ({
        label: event.event_type || event.type || "Upcoming class",
        note: formatTime(event.scheduled_start || event.start_time) + " · " + (event.room_id || "Room unassigned"),
        status: "UNKNOWN", target: "#studio-schedule",
      })),
    ];
    const remaining = Math.max(0, incidents.length + upcoming.length - context.length);
    if (remaining) context.push({ label: "+" + remaining + " more", note: "Open full view", status: "UNKNOWN", target: incidents.length ? "#studio-attention" : "#studio-schedule" });
    return {
      label: "MANAGER · OPERATIONS", value: String(incidents.length), unit: "actionable exceptions",
      summary: incidents.length ? incidents.length + " exception(s) need review." : upcoming.length ? "No reported incidents; upcoming classes are listed for review." : "No current exceptions or upcoming classes are available.",
      status: incidents.length ? "DEGRADED" : "UNKNOWN",
      ring: incidents.length ? "conic-gradient(var(--warn) 0deg 120deg, var(--line) 120deg 360deg)" : "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)",
      contextTitle: "EXCEPTIONS & UPCOMING", context,
      primary: incidents.length ? "Review exceptions" : "Open today",
      primaryTarget: incidents.length ? "#studio-attention" : "#studio-schedule",
      chips: [
        { title: "Today's classes", detail: "Schedule and attendance", target: "#studio-schedule", state: upcoming.length + " upcoming" },
        { title: "Incidents", detail: "Review operational attention", target: "#studio-attention", state: incidents.length ? incidents.length + " open" : "None reported" },
        { title: "Studio spaces", detail: "Rooms and capabilities", target: "#studio-spaces", state: "Open" },
        { title: "Reports", detail: "Reports destination is not available in this portal view", disabled: true, state: "Planned" },
      ],
      dock: [{ label: "Applications", target: "#studio-applications" }, { label: "Studio map", target: "#studio-spaces" }, { label: "Schedule", target: "#studio-schedule" }, ...serviceDock(applications, applicationsStale, applicationsLoading)],
    };
  }

  return {
    label: role.toUpperCase() + " · STUDIO OVERVIEW",
    value: incidents.length ? String(incidents.length) : String(count),
    unit: incidents.length ? "attention items" : "scheduled classes",
    summary: incidents.length ? "Operational attention is required." : count ? "Schedule and studio state are available." : "No schedule summary is currently available.",
    status: incidents.length ? "DEGRADED" : "UNKNOWN",
    ring: incidents.length ? "conic-gradient(var(--warn) 0deg 120deg, var(--line) 120deg 360deg)" : "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)",
    contextTitle: "ATTENTION",
    context: [
      { label: "Scheduled classes", note: String(count), status: summary?.state_authoritative === true ? "VERIFIED" : "UNKNOWN" },
      { label: "Incidents", note: String(incidents.length), status: incidents.length ? "DEGRADED" : "UNKNOWN" },
    ],
    primary: incidents.length ? "Review exceptions" : "View today's schedule",
    primaryTarget: incidents.length ? "#studio-attention" : "#studio-schedule",
    chips: [
      { title: "Today's classes", detail: "Schedule and attendance", target: "#studio-schedule", state: count + " listed" },
      { title: "Incidents", detail: "Review attention items", target: "#studio-attention", state: incidents.length ? incidents.length + " open" : "None reported" },
      { title: "Studio spaces", detail: "Rooms and capabilities", target: "#studio-spaces", state: "Open" },
      { title: "Applications", detail: "Studio tools and readiness", target: "#studio-applications", state: applications?.length ? applications.length + " configured" : "Unknown" },
    ],
    dock: [{ label: "Applications", target: "#studio-applications" }, { label: "Studio map", target: "#studio-spaces" }, { label: "Schedule", target: "#studio-schedule" }, ...serviceDock(applications, applicationsStale, applicationsLoading)],
  };
}

function StatusDot({ status }) {
  return <span className={"cockpit-status-dot cockpit-status-" + String(status || "UNKNOWN").toLowerCase()} aria-hidden="true" />;
}

export default function StudioCockpit(props) {
  const [selectedAdminSet, setSelectedAdminSet] = useState("Infrastructure");
  const [connectorGeometry, setConnectorGeometry] = useState(null);
  const [finishedAckVisible, setFinishedAckVisible] = useState(props.eventState === "FINISHED");
  const stageRef = useRef(null);
  const dialRef = useRef(null);
  const chipsRef = useRef(null);
  const role = roleFor(props.user);
  const hideFinishedAck = role === "Trainer" && props.eventState === "FINISHED" && !finishedAckVisible;
  const safeSchedule = Array.isArray(props.schedule) ? props.schedule.filter((item) => item && typeof item === "object" && !Array.isArray(item)) : [];
  const safeIncidents = Array.isArray(props.incidents) ? props.incidents.filter((item) => item && typeof item === "object" && !Array.isArray(item)) : [];
  const safeApplications = Array.isArray(props.applications) ? props.applications.filter((item) => item && typeof item === "object" && !Array.isArray(item)) : [];
  const safeReadiness = props.readiness && typeof props.readiness === "object" && !Array.isArray(props.readiness) ? props.readiness : {};
  const hasUpcomingClass = safeSchedule.some((event) => {
    const start = event?.scheduled_start || event?.start_time;
    return start && new Date(start).getTime() >= Date.now();
  });
  const presentationProps = hideFinishedAck
    ? { ...props, schedule: safeSchedule, incidents: safeIncidents, applications: safeApplications, readiness: safeReadiness, eventState: "IDLE", cls: null, dominantState: hasUpcomingClass ? "UPCOMING" : "NO CLASS" }
    : { ...props, schedule: safeSchedule, incidents: safeIncidents, applications: safeApplications, readiness: safeReadiness };
  const content = getRoleContent({ ...presentationProps, role, adminSegmentSet: selectedAdminSet });
  const go = (target) => {
    if (typeof target !== "string" || !target.startsWith("#")) return;
    const node = document.querySelector(target);
    if (node) {
      const details = node.closest("details.studio-legacy-workflows");
      if (details && !details.open) details.open = true;
      if (target === "#studio-schedule" && props.onSchedule) props.onSchedule();
      window.requestAnimationFrame(() => node.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start",
      }));
    }
  };

  useEffect(() => {
    if (role !== "Trainer" || props.eventState !== "FINISHED") {
      setFinishedAckVisible(false);
      return undefined;
    }
    setFinishedAckVisible(true);
    const timer = window.setTimeout(() => setFinishedAckVisible(false), 5000);
    return () => window.clearTimeout(timer);
  }, [role, props.eventState, props.cls?.id]);

  useEffect(() => {
    const stage = stageRef.current;
    const dial = dialRef.current;
    const chips = chipsRef.current;
    if (!stage || !dial || !chips) return undefined;
    let frame = 0;
    const update = () => {
      if (window.matchMedia("(max-width: 1000px)").matches) {
        setConnectorGeometry(null);
        return;
      }
      const stageRect = stage.getBoundingClientRect();
      const dialRect = dial.getBoundingClientRect();
      const chipNodes = Array.from(chips.querySelectorAll(".cockpit-chip"));
      const paths = chipNodes.map((chip) => {
        const rect = chip.getBoundingClientRect();
        const x1 = Math.max(0, dialRect.right - stageRect.left - 5);
        const y1 = dialRect.top - stageRect.top + dialRect.height / 2;
        const x2 = Math.max(x1, rect.left - stageRect.left + 2);
        const y2 = rect.top - stageRect.top + rect.height / 2;
        const curve = Math.max(18, (x2 - x1) * 0.42);
        return `M ${x1.toFixed(1)} ${y1.toFixed(1)} C ${(x1 + curve).toFixed(1)} ${y1.toFixed(1)}, ${(x2 - curve).toFixed(1)} ${y2.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
      });
      const next = { width: Math.max(1, stageRect.width), height: Math.max(1, stageRect.height), paths };
      setConnectorGeometry((previous) => previous
        && previous.width === next.width
        && previous.height === next.height
        && previous.paths.join("|") === next.paths.join("|") ? previous : next);
    };
    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };
    scheduleUpdate();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleUpdate);
    if (observer) [stage, dial, chips, ...chips.querySelectorAll(".cockpit-chip")].forEach((node) => observer.observe(node));
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", scheduleUpdate);
      observer?.disconnect();
    };
  }, [role, selectedAdminSet, props.applications?.length, props.stale]);

  const hasFreshness = Boolean(props.lastRefreshedAt);
  const stale = hasFreshness && Boolean(props.stale);
  const authoritativeState = props.summary?.state_authoritative === true;
  const unknownFreshness = !hasFreshness || stale || !authoritativeState;
  const status = unknownFreshness ? "UNKNOWN" : (content.status || "UNKNOWN");
  const branding = resolveBranding(props.summary?.branding);
  const studioName = branding.deployment?.name || props.summary?.studio?.name || "Pilates d’Luxe";
  const studioDescriptor = branding.deployment?.descriptor || "An Adaptive Pilates & Wellness Studio";
  const primaryAction = () => {
    if (content.primaryAction === "doctor" && props.onDoctor) return props.onDoctor();
    if (role === "Trainer") {
      if (["RUNNING", "STARTED"].includes(props.eventState) && props.onPause) return props.onPause();
      if (props.eventState === "PAUSED" && props.onResume) return props.onResume();
      if (props.eventState === "SCHEDULED" && props.cls?.startable === true && props.readiness?.ready === true && props.summary?.state_authoritative === true && props.onStart) return props.onStart();
    }
    return go(content.primaryTarget);
  };
  return (
    <section className="studio-cockpit" aria-label={role + " cockpit"}>
      <div className="cockpit-topline">
        <div className="cockpit-brand-block">{!props.hidePlatformKicker && <span className="cockpit-brand-kicker">HACaMS · SPACE OPERATIONS</span>}<h2>{studioName}</h2><p>{studioDescriptor}</p><p className="cockpit-refresh-meta" aria-live="polite">{!hasFreshness ? "Waiting for first successful refresh" : <>{stale && <strong className="cockpit-stale-label">STALE · </strong>}Last successful refresh {new Date(props.lastRefreshedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}{stale ? " · Current health is UNKNOWN" : ""}</>}{props.refreshError ? " · Refresh issue: " + props.refreshError : ""}</p></div>
        <div className="cockpit-topline-actions">
          {props.user && <StudioProfileMenu
            user={props.user}
            displayName={props.profileDisplayName}
            profileInitials={props.profileInitials}
            roleLabel={role}
            completionLabel={props.profileCompletion}
            onEditProfile={props.onEditProfile}
            onSignOut={props.onSignOut}
          />}
          <span className={"cockpit-overall-status cockpit-status-" + status.toLowerCase()}><StatusDot status={status} />{status}</span>
        </div>
      </div>
      <div className="cockpit-stage" ref={stageRef}>
        {connectorGeometry && connectorGeometry.paths.length > 0 && <svg className="cockpit-connectors" viewBox={`0 0 ${connectorGeometry.width} ${connectorGeometry.height}`} preserveAspectRatio="none" aria-hidden="true">{connectorGeometry.paths.map((path, index) => <path key={index} d={path} />)}</svg>}
        <aside className="cockpit-context" aria-label={content.contextTitle}>
          {["Platform Admin", "Admin"].includes(role) && (
            <div className="cockpit-segment-toggle" role="group" aria-label="Health segment set">
              {["Infrastructure", "Platform"].map((setName) => (
                <button key={setName} type="button" aria-pressed={selectedAdminSet === setName} onClick={() => setSelectedAdminSet(setName)}>
                  {setName}
                </button>
              ))}
            </div>
          )}
          <div className="cockpit-section-label">{content.contextTitle}</div>
          {content.context.length ? content.context.map((item, index) => item.target ? (
            <button className="cockpit-context-pill cockpit-context-pill-link" type="button" key={item.label + index} onClick={() => go(item.target)}>
              <StatusDot status={unknownFreshness ? "UNKNOWN" : item.status} />
              <span>{item.label}</span><small>{item.note}</small>
            </button>
          ) : (
            <div className="cockpit-context-pill" key={item.label + index}>
              <StatusDot status={unknownFreshness ? "UNKNOWN" : item.status} />
              <span>{item.label}</span><small>{item.note}</small>
            </div>
          )) : <p className="cockpit-muted">No current context reported.</p>}
        </aside>
        <div className="cockpit-dial-column">
          <div ref={dialRef} className={"cockpit-dial cockpit-dial-" + status.toLowerCase()} style={{ "--cockpit-ring": unknownFreshness ? "conic-gradient(var(--cockpit-status-unknown, #85838d) 0deg 360deg)" : content.ring }}>
            <div className="cockpit-dial-core">
              <div className="cockpit-section-label">{content.label}</div>
              <div className="cockpit-dial-value">{content.value}</div>
              <div className="cockpit-dial-unit">{content.unit}</div>
              <p>{stale ? "Last known values are shown for context only; fresh evidence is required." : !hasFreshness ? "Waiting for the first successful refresh; health is UNKNOWN." : !authoritativeState ? "The current projection is not authoritative; health remains UNKNOWN." : content.summary}</p>
              <button className="cockpit-primary-action" type="button" onClick={primaryAction} disabled={Boolean(props.actionBusy || props.doctorRunning)}>{props.doctorRunning && content.primaryAction === "doctor" ? "Running doctor…" : content.primary}</button>
              {role === "Trainer" && ["RUNNING", "STARTED", "PAUSED"].includes(props.eventState) && props.onFinish && (
                <button className="cockpit-secondary-action" type="button" onClick={props.onFinish} disabled={Boolean(props.actionBusy)}>Finish class</button>
              )}
            </div>
          </div>
        </div>
        <div className="cockpit-chips" aria-label="Primary destinations" ref={chipsRef}>
          {content.chips.map((chip) => {
            const children = <>
              <span className="cockpit-chip-mark" aria-hidden="true">{chip.title.slice(0, 1)}</span>
              <span className="cockpit-chip-copy"><strong>{chip.title}</strong><small>{chip.detail}</small></span>
              <span className="cockpit-chip-state">{chip.state}</span><span aria-hidden="true" className="cockpit-chip-arrow">›</span>
            </>;
            if (chip.href && !chip.disabled) return <a className={"cockpit-chip" + (chip.serviceId ? " cockpit-service-" + chip.serviceId : "")} key={chip.title} href={chip.href} target="_blank" rel="noopener noreferrer">{children}</a>;
            if (chip.disabled) return <button type="button" className={"cockpit-chip cockpit-chip-disabled" + (chip.serviceId ? " cockpit-service-" + chip.serviceId : "")} key={chip.title} disabled aria-disabled="true">{children}</button>;
            return <button className="cockpit-chip" type="button" key={chip.title} onClick={() => go(chip.target)}>{children}</button>;
          })}
        </div>
      </div>
      <nav className="cockpit-dock" aria-label="Secondary destinations">
        {content.dock.map((item) => item.disabled ? <button type="button" className={"cockpit-dock-disabled" + (item.serviceId ? " cockpit-service-" + item.serviceId : "")} key={item.label} disabled><span>{item.label}</span>{item.detail && <small>{item.detail}</small>}</button> : item.href ? <a className={"cockpit-dock-link" + (item.serviceId ? " cockpit-service-" + item.serviceId : "")} key={item.label} href={item.href} target={item.external ? "_blank" : undefined} rel={item.external ? "noopener noreferrer" : undefined}><span>{item.label}</span>{item.detail && <small>{item.detail}</small>}</a> : <button type="button" key={item.label} onClick={() => go(item.target)}><span>{item.label}</span>{item.detail && <small>{item.detail}</small>}</button>)}
        <span className="cockpit-dock-role">{role}</span>
      </nav>
      {["Platform Admin", "Admin"].includes(role) && (props.doctorRunning || props.doctorResult || props.doctorError) && (
        <aside className="cockpit-doctor-drawer" aria-label="Read-only doctor report" aria-live="polite">
          <div className="cockpit-doctor-drawer-heading">
            <div>
              <span className="cockpit-section-label">READ-ONLY DOCTOR</span>
              <h3>{props.doctorRunning ? "Checking current state…" : props.doctorResult ? "Diagnostic report" : "Doctor could not complete"}</h3>
            </div>
            <button type="button" onClick={props.onCloseDoctor} disabled={Boolean(props.doctorRunning)} aria-label="Close doctor report">Close</button>
          </div>
          {props.doctorRunning && <p role="status">Inspecting authoritative state, capabilities, applications and incidents. No remediation is performed.</p>}
          {props.doctorError && <p className="cockpit-doctor-error" role="alert">{props.doctorError}</p>}
          {props.doctorResult && <>
            <p className="cockpit-doctor-summary">Overall status: <strong>{props.doctorResult.overall_status || "UNKNOWN"}</strong> · Revision {props.doctorResult.state_revision ?? "unavailable"}</p>
            <p className="cockpit-doctor-meta">Generated {props.doctorResult.generated_at ? new Date(props.doctorResult.generated_at).toLocaleString() : "time unavailable"}. No remediation commands were run.</p>
            <div className="studio-doctor-checks">
              {(Array.isArray(props.doctorResult.checks) ? props.doctorResult.checks : []).map((check, index) => (
                <div className={"studio-doctor-check studio-doctor-check-" + String(check.status || "UNKNOWN").toLowerCase()} key={String(check.name || "check") + index}>
                  <strong>{check.name || "Diagnostic check"}</strong>
                  <span>{check.status || "UNKNOWN"}</span>
                  <small>{check.detail || "No detail supplied."}</small>
                </div>
              ))}
            </div>
          </>}
        </aside>
      )}
      <footer className="cockpit-footer" aria-label="Authoritative state and deployment provenance">
        <span>{props.summary?.state_authoritative === true ? "Authoritative state" : "State projection"}{props.summary?.state_revision !== undefined && props.summary?.state_revision !== null ? ` · Rev ${props.summary.state_revision}` : " · revision unavailable"}</span>
        <span>{typeof props.deploymentIdentity?.version === "string" ? `HACaMS ${props.deploymentIdentity.version}` : "Deployment version unavailable"}</span>
        <span className={"cockpit-provenance cockpit-provenance-" + (props.deploymentIdentity?.status === "VERIFIED" ? "verified" : !props.deploymentIdentity || props.deploymentIdentity?.status === "UNAVAILABLE" ? "unavailable" : "unverified")}>Deployment provenance: {props.deploymentIdentity?.status === "VERIFIED" ? "VERIFIED" : !props.deploymentIdentity || props.deploymentIdentity?.status === "UNAVAILABLE" ? "UNAVAILABLE" : "UNVERIFIED"}</span>
      </footer>
    </section>
  );
}
